import { Command } from 'commander';
import ora from 'ora';
import prompts from 'prompts';
import { WorkflowManager } from '../core/workflow-manager';
import { ProjectConfigManager } from '../core/project-config.js';
import { logger } from '../utils/logger';
import { hookSystem } from '../claude/hook-system.js';
import { ContextInjector } from '../claude/context-injector.js';
import path from 'path';

export function createInitCommand(): Command {
  const command = new Command('init');

  command
    .description('Initialize workflow system in current project')
    .option('-f, --force', 'Force overwrite existing workflow configuration')
    .option('-y, --yes', 'Skip confirmation prompts')
    .option('--no-ai', 'Disable AI features')
    .option('--claude', 'Enable Claude Code integration (auto-detected if .claude exists)')
    .option('--no-hooks', 'Disable Claude Code hooks configuration')
    .option('--project-id <id>', 'Specify project ID (auto-generated if not provided)')
    .option('--project-name <name>', 'Specify project name (uses directory name if not provided)')
    .option('--project-desc <desc>', 'Specify project description')
    .action(async (options) => {
      try {
        const manager = new WorkflowManager({
          enableAI: options.ai !== false
        });

        // Check if already initialized
        if (await manager.isInitialized() && !options.force) {
          if (!options.yes) {
            const { confirm } = await prompts({
              type: 'confirm',
              name: 'confirm',
              message: 'Workflow system already initialized. Overwrite?',
              initial: false
            });

            if (!confirm) {
              logger.info('Initialization cancelled');
              return;
            }
          }
        }

        const spinner = ora('Initializing workflow system...').start();

        try {
          // Set environment variable if Claude integration is requested
          if (options.claude) {
            process.env.CLAUDE_CODE = 'true';
          }
          if (options.hooks === false) {
            process.env.WORKFLOW_NO_HOOKS = 'true';
          }

          await manager.initialize(options.force);
          
          // Initialize project configuration
          const projectConfig = new ProjectConfigManager(manager.workflowDir);
          const projectInfo = await projectConfig.init({
            id: options.projectId,
            name: options.projectName,
            description: options.projectDesc,
          });
          
          spinner.succeed('Workflow system initialized successfully!');

          logger.info('');
          logger.info(`Project: ${projectInfo.project.name} (ID: ${projectInfo.project.id})`);
          if (projectInfo.project.description) {
            logger.info(`Description: ${projectInfo.project.description}`);
          }
          
          logger.info('');
          logger.info('🚀 Next steps:');
          logger.info('');
          logger.info('📝 Command line (fast operations):');
          logger.info('  • workflow create "task description"  - Create a new task');
          logger.info('  • workflow next                       - Start next task');
          logger.info('  • workflow done                       - Complete current task');
          logger.info('  • workflow list                       - View all tasks');
          
          logger.info('');
          logger.info('🎨 Visual interface (full features):');
          logger.info('  • workflow ui                         - Launch Web UI');
          logger.info('    - Kanban board for task management');
          logger.info('    - Flowgram for process visualization');
          logger.info('    - Agent system for AI assistance');
          logger.info('    - PRD editor and more!');
          
          logger.info('');
          logger.info('🔄 Real-time synchronization:');
          logger.info('  • Automatic sync between CLI, Web UI, and VSCode');
          logger.info('  • Smart sync strategies for optimal performance');
          logger.info('  • Offline support with automatic retry');
          logger.info('  • workflow sync-status                - View sync status');
          
          // Add Claude-specific instructions if integrated
          if (process.env.CLAUDE_CODE || await manager.isClaudeIntegrated()) {
            logger.info('');
            logger.info('🤖 Claude Code integration:');
            
            // 初始化Claude Hook系统
            if (options.hooks !== false) {
              spinner.text = 'Setting up Claude hooks...';
              try {
                await hookSystem.initialize();
                
                // 生成初始上下文
                const contextInjector = new ContextInjector();
                await contextInjector.generateContext();
                
                logger.info('  ✅ Claude Hook系统已初始化');
                logger.info('  ✅ AI上下文已生成');
                logger.info('');
                logger.info('  📂 Hook文件: .claude/hooks/');
                logger.info('  📄 上下文文件: .claude/CONTEXT.md');
                logger.info('');
                logger.info('  • workflow hook list                  - 查看所有Hook');
                logger.info('  • workflow ai-context                 - 更新AI上下文');
                logger.info('  • workflow hook test before_session   - 测试Hook');
              } catch (error: any) {
                logger.warn(`  ⚠️  Hook系统初始化失败: ${error.message}`);
              }
            }
            
            logger.info('');
            logger.info('  • Use /task command to manage tasks');
            logger.info('  • Use /kb command to search knowledge base');
            logger.info('  • Use /workflow-init to reinitialize');
          }
          
          logger.info('');
          logger.info('📚 Learn more:');
          logger.info('  • workflow --help                     - View all commands');
          logger.info('  • https://github.com/gongfu/gongfu   - Documentation');
        } catch (error) {
          spinner.fail('Failed to initialize workflow system');
          throw error;
        }
      } catch (error: any) {
        logger.error('Initialization failed:', error.message);
        if (process.env.DEBUG) {
          console.error(error);
        }
        process.exit(1);
      }
    });

  return command;
}